import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { Treemap, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { readableInkOn } from "../graphlab/graphShared";
import TextBlock from "./TextBlock";
import TextBlockTwo from "./TextBlockTwo";
import { COLORS } from "../../utils/treemapUtils";
import { glossTitle } from "../../utils/termGlosses";
import i18n from "../../i18n";
import {
  mergeTermCounts,
  setsForTerm,
  topTerms,
  SetTermCounts,
} from "../../utils/evidenceAggregateUtils";
import { siteUrl } from "../../utils/constants";

/** Chapter treemap of cultural-practice formats, powered by the live
 *  evidence API instead of hand-entered CMS numbers: counts are pages
 *  per term summed across every archived collection (the chapter is
 *  about formats — provenance is deliberately not shown), and clicking a
 *  format streams ALL of its instances with archive links, paged through
 *  every collection. The CMS `heading` and `content` (rich blocks,
 *  rendered under the heading) are editorial; `bubbleText` is ignored. */

const FORMATS_CATEGORY = 1; // "Cultural practices" in the evidence lexicon
const TOP_FORMATS = 14;
const INK = "#212529";
const inkLink = { color: INK,
  textDecoration: "underline", textUnderlineOffset: 2 } as const;

interface TreemapChild {
  name: string;
  size: number;
}

interface CustomContentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  size?: number;
  onClick: (name: string) => void;
  selected?: string | null;
  root?: { children: TreemapChild[] };
}

const CustomContent: React.FC<CustomContentProps> = (props) => {
  const {
    x = 0, y = 0, width = 0, height = 0,
    root, name = "", size, onClick, selected,
  } = props;
  const colorIndex =
    root?.children?.findIndex((child) => child.name === name) ?? -1;
  const color = colorIndex >= 0 ? COLORS[colorIndex % COLORS.length] : COLORS[0];
  const isSelected = !!selected && selected === name;

  const tooltip = glossTitle(name.toLowerCase(), i18n.language);
  return (
    <g onClick={() => onClick(name)}>
      {tooltip && <title>{tooltip}</title>}
      <rect
        x={x} y={y} width={width} height={height}
        fill={color}
        style={{ cursor: "pointer", transition: "all 0.3s ease" }}
      />
      {width > 58 && height > 34 && (
        <text
          x={x + width / 2} y={y + height / 2}
          textAnchor="middle" fill="#1A1A1A"
          fontSize={14} fontWeight={isSelected ? "bold" : "normal"}
        >
          {name}
          <tspan x={x + width / 2} dy="1.2em" fontSize={12} fill="#333">
            {size?.toLocaleString()}
          </tspan>
        </text>
      )}
    </g>
  );
};

interface Hit {
  id: number;
  url: string;
  crawl_date: string;
  snippet: string;
  captures: number;
  wayback: string;
}

/** Term matches arrive marked with \x01/\x02 sentinels; render them as
 *  <mark> without ever interpreting server text as HTML. The highlight
 *  wears the selected treemap square's color, tying the panels together. */
const Snippet = ({ text, color }: { text: string; color: string }) => (
  <>
    {String(text).split("\x01").map((chunk, i) => {
      if (i === 0) return <React.Fragment key={i}>{chunk}</React.Fragment>;
      const [hit, ...rest] = chunk.split("\x02");
      return (
        <React.Fragment key={i}>
          <mark style={{ background: color, color: readableInkOn(color),
            padding: "0 3px", borderRadius: 3 }}>
            {hit}
          </mark>
          {rest.join("\x02")}
        </React.Fragment>
      );
    })}
  </>
);

interface TreemapChartProps {
  container?: {
    heading?: string;
    content?: import("../../types/strapi").StrapiBlock[];
  };
}

const PAGE_SIZE = 20;

const TreemapChart: React.FC<TreemapChartProps> = ({ container }) => {
  const { t } = useTranslation();
  const api = `${siteUrl}/api/evidence`;
  const [perSet, setPerSet] = useState<SetTermCounts[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [hits, setHits] = useState<Hit[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  // Stream cursor: which collection we are paging through, and where.
  const cursorRef = useRef<{ sets: { set: string; pages: number }[];
    setIndex: number; page: number; done: boolean }>({
      sets: [], setIndex: 0, page: 0, done: true,
    });
  const runSeq = useRef(0);
  const textContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${api}/sets`)
      .then(async ({ data: sets }: { data: { name: string }[] }) => {
        const lists: SetTermCounts[] = await Promise.all(sets.map(
          async ({ name }) => ({
            set: name,
            terms: (await axios.get(`${api}/terms`, {
              params: { set: name, category: FORMATS_CATEGORY },
            })).data,
          })));
        if (!cancelled) setPerSet(lists);
      })
      .catch(() => { if (!cancelled) setUnavailable(true); });
    return () => { cancelled = true; };
  }, [api]);

  const merged = useMemo(
    () => topTerms(mergeTermCounts(perSet), TOP_FORMATS), [perSet]);

  const data = useMemo(() => [{
    name: "Formats",
    children: merged.map(({ term, pages }) => ({ name: term, size: pages })),
  }], [merged]);

  /** Fetch the next batch for the current selection, walking collections
   *  biggest-first until every instance has been offered. */
  const loadMore = useCallback(async (term: string, reset: boolean) => {
    const mySeq = reset ? runSeq.current + 1 : runSeq.current;
    runSeq.current = mySeq;
    const cursor = reset
      ? { sets: setsForTerm(perSet, term), setIndex: 0, page: 0, done: false }
      : cursorRef.current;
    if (reset) {
      cursorRef.current = cursor;
      setHits([]);
      setTotalPages(cursor.sets.reduce((sum, s) => sum + s.pages, 0));
    }
    if (cursor.done || cursor.setIndex >= cursor.sets.length) {
      cursor.done = true;
      return;
    }
    setLoading(true);
    try {
      const { set } = cursor.sets[cursor.setIndex];
      const { data: page } = await axios.get(`${api}/excerpts`, {
        params: { set, term, page: cursor.page },
      });
      if (runSeq.current !== mySeq) return;
      const batch: Hit[] = page.hits.map((h: Omit<Hit, "wayback">) => ({
        ...h, wayback: page.wayback,
      }));
      setHits((prev) => (reset ? batch : [...prev, ...batch]));
      if (page.offset + page.hits.length >= page.total) {
        cursor.setIndex += 1;
        cursor.page = 0;
        cursor.done = cursor.setIndex >= cursor.sets.length;
      } else {
        cursor.page += 1;
      }
    } catch {
      cursor.done = true;
    } finally {
      if (runSeq.current === mySeq) setLoading(false);
    }
  }, [api, perSet]);

  const handleClick = useCallback((term: string) => {
    setSelected(term);
    loadMore(term, true);
  }, [loadMore]);

  // Auto-select the biggest format once counts arrive.
  useEffect(() => {
    if (merged.length > 0 && !selected) handleClick(merged[0].term);
  }, [merged, selected, handleClick]);

  useEffect(() => {
    if (textContainerRef.current) textContainerRef.current.scrollTop = 0;
  }, [selected]);

  if (unavailable) {
    return (
      <>
        <TextBlock caseTitle={container?.heading} cssClass="mt--100 " />
        <p className="text-center mt-5">
          {t("evidence.unavailable", "The evidence service is not available.")}
        </p>
      </>
    );
  }

  const exhausted = cursorRef.current.done;
  // The selected square's color carries into the right panel (header
  // underline + excerpt highlights) so the two halves read as one unit.
  const selectedIndex = merged.findIndex((m) => m.term === selected);
  const selectedColor =
    selectedIndex >= 0 ? COLORS[selectedIndex % COLORS.length] : "#4B9CD3";

  return (
    <>
      <TextBlock caseTitle={container?.heading} cssClass="mt--100 " />
      {container?.content && (
        <div className="evd-treemap-lede">
          <style>{`
            .evd-treemap-lede { margin-top: 16px; }
            .evd-treemap-lede .inner p { margin-bottom: 8px; }
          `}</style>
          <TextBlockTwo content={container.content} />
        </div>
      )}
      <div className="container">
        <div className="row">
          <div className="col-lg-6 p-0 mt-xl-5">
            <div style={{ height: "800px" }}>
              <ResponsiveContainer>
                <Treemap
                  data={data}
                  dataKey="size"
                  stroke="none"
                  isAnimationActive={false}
                  content={(
                    <CustomContent onClick={handleClick} selected={selected} />
                  )}
                />
              </ResponsiveContainer>
            </div>
            <p className="text-center mt-2" style={{ fontSize: 13, color: "#757575" }}>
              {t("evidence.treemapMetric",
                "archived pages mentioning each format, across all collections")}
            </p>
          </div>
          <div className="col-lg-6 p-0 mt-xl-5">
            <div
              ref={textContainerRef}
              style={{
                height: "800px", overflowY: "auto", padding: "16px",
                backgroundColor: "#F0F0F0", color: "#1A1A1A", borderRadius: "4px",
              }}
            >
              {selected && (
                <div style={{ borderBottom: `4px solid ${selectedColor}`,
                  paddingBottom: 8, marginBottom: 16, textAlign: "center" }}>
                  <h2 style={{ color: "#1A1A1A", marginBottom: 4 }}>
                    {selected.charAt(0).toUpperCase() + selected.slice(1)}
                  </h2>
                  <span style={{ fontSize: 14, color: "#555" }}>
                    {totalPages.toLocaleString()}{" "}
                    {t("evidence.pagesMention", "pages mention")}{" "}
                    “{selected}”
                  </span>
                  {" · "}
                  <Link
                    to={`/evidence/graph?term=${encodeURIComponent(selected)}`}
                    style={{ fontSize: 14, ...inkLink }}
                  >
                    {t("evidence.exploreNetwork", "explore in the evidence network")} →
                  </Link>
                </div>
              )}
              {hits.map((hit) => (
                <div
                  key={`${hit.id}-${hit.url}`}
                  style={{ background: "#fff", border: "1px solid #dee2e6",
                    borderRadius: 4, padding: "14px 16px", marginBottom: 14 }}
                >
                  <p style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
                    <Snippet text={hit.snippet} color={selectedColor} />
                  </p>
                  <div style={{ fontSize: 12, color: "#757575",
                    overflowWrap: "anywhere" }}>
                    <a href={`${hit.wayback}${hit.crawl_date || "*"}/${hit.url}`}
                      target="_blank" rel="noreferrer"
                      style={inkLink}>
                      {t("evidence.capture", "original as crawled")}
                    </a>
                    {" · "}
                    <a href={hit.url} target="_blank" rel="noreferrer"
                      title={hit.url} style={inkLink}>
                      {t("evidence.source", "source page")}
                    </a>
                    {hit.captures > 1 && (
                      <span> · {hit.captures} {t("evidence.captures", "captures")}</span>
                    )}
                  </div>
                </div>
              ))}
              {selected && !exhausted && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => selected && loadMore(selected, false)}
                  style={{ display: "block", width: "100%", padding: "10px 0",
                    border: "1px solid #dee2e6", background: "#fff",
                    color: INK, cursor: "pointer", borderRadius: 4 }}
                >
                  {loading
                    ? t("evidence.loading", "loading…")
                    : t("evidence.loadMore", "Load more")}
                </button>
              )}
              {selected && exhausted && hits.length > 0 && (
                <p className="text-center" style={{ color: "#757575", fontSize: 13 }}>
                  {t("evidence.allInstancesShown", "all instances shown")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TreemapChart;
